/**
 * Сервис для работы с локальным хранилищем
 */

// Базовый интерфейс без сохранения документов
interface StoredSession {
  token: string;
  timestamp: number;
}

// Интерфейс для хранения черновика с отслеживанием времени
interface DraftStorage<T> {
  data: T;
  timestamp: number;
}

class LocalStorageService {
  private readonly USER_SESSION_KEY = 'dissai_user_session';
  private readonly CONTENT_PLAN_DRAFT_KEY = 'dissai_content_plan_draft';
  private readonly REELS_TRANSCRIPTION_DRAFT_KEY = 'dissai_reels_transcription_draft';
  private readonly AUDIENCE_DRAFT_KEY = 'dissai_audience_draft';
  private readonly ROADMAP_DRAFT_KEY = 'dissai_roadmap_draft';
  
  // Для защиты от дублирования сохранений и предотвращения потери данных
  private lastSavedData: {[key: string]: string} = {};

  /**
   * Сохранение сессии пользователя
   */
  saveUserSession(token: string): void {
    try {
      const sessionData: StoredSession = {
        token,
        timestamp: Date.now()
      };
      localStorage.setItem(this.USER_SESSION_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.error('Ошибка при сохранении сессии пользователя:', error);
    }
  }

  /**
   * Получение токена сессии пользователя
   */
  getUserSessionToken(): string | null {
    try {
      const sessionData = localStorage.getItem(this.USER_SESSION_KEY);
      if (!sessionData) return null;
      
      const parsedData = JSON.parse(sessionData) as StoredSession;
      return parsedData.token;
    } catch (error) {
      console.error('Ошибка при получении сессии пользователя:', error);
      return null;
    }
  }

  /**
   * Очистка сессии пользователя
   */
  clearUserSession(): void {
    try {
      localStorage.removeItem(this.USER_SESSION_KEY);
    } catch (error) {
      console.error('Ошибка при очистке сессии пользователя:', error);
    }
  }

  /**
   * Сохранение черновика формы
   */
  saveDraft<T>(key: string, data: T): void {
    try {
      const draftStorage: DraftStorage<T> = {
        data,
        timestamp: Date.now()
      };
      
      const jsonData = JSON.stringify(draftStorage);
      
      // Проверяем, не сохраняем ли мы те же данные повторно
      // Это предотвращает ненужные обновления localStorage и защищает от проблем с StrictMode
      if (this.lastSavedData[key] === jsonData) {
        return;
      }
      
      localStorage.setItem(key, jsonData);
      this.lastSavedData[key] = jsonData;
    } catch (error) {
      console.error(`Ошибка при сохранении черновика (${key}):`, error);
    }
  }

  /**
   * Получение черновика формы
   */
  getDraft<T>(key: string): T | null {
    try {
      const storedData = localStorage.getItem(key);
      if (!storedData) return null;
      
      const parsedData = JSON.parse(storedData) as DraftStorage<T>;
      
      // Кэшируем последнее состояние для предотвращения повторного сохранения тех же данных
      this.lastSavedData[key] = storedData;
      
      return parsedData.data;
    } catch (error) {
      console.error(`Ошибка при получении черновика (${key}):`, error);
      return null;
    }
  }

  /**
   * Очистка черновика формы
   */
  clearDraft(key: string): void {
    try {
      localStorage.removeItem(key);
      // Очищаем кэш последнего сохраненного состояния
      delete this.lastSavedData[key];
    } catch (error) {
      console.error(`Ошибка при очистке черновика (${key}):`, error);
    }
  }

  // Методы для работы с конкретными черновиками
  saveContentPlanDraft<T>(data: T): void {
    this.saveDraft(this.CONTENT_PLAN_DRAFT_KEY, data);
  }

  getContentPlanDraft<T>(): T | null {
    return this.getDraft<T>(this.CONTENT_PLAN_DRAFT_KEY);
  }

  clearContentPlanDraft(): void {
    this.clearDraft(this.CONTENT_PLAN_DRAFT_KEY);
  }

  saveReelsTranscriptionDraft<T>(data: T): void {
    this.saveDraft(this.REELS_TRANSCRIPTION_DRAFT_KEY, data);
  }

  getReelsTranscriptionDraft<T>(): T | null {
    return this.getDraft<T>(this.REELS_TRANSCRIPTION_DRAFT_KEY);
  }

  clearReelsTranscriptionDraft(): void {
    this.clearDraft(this.REELS_TRANSCRIPTION_DRAFT_KEY);
  }

  saveAudienceDraft<T>(data: T): void {
    this.saveDraft(this.AUDIENCE_DRAFT_KEY, data);
  }

  getAudienceDraft<T>(): T | null {
    return this.getDraft<T>(this.AUDIENCE_DRAFT_KEY);
  }

  clearAudienceDraft(): void {
    this.clearDraft(this.AUDIENCE_DRAFT_KEY);
  }

  saveRoadmapDraft<T>(data: T): void {
    this.saveDraft(this.ROADMAP_DRAFT_KEY, data);
  }

  getRoadmapDraft<T>(): T | null {
    return this.getDraft<T>(this.ROADMAP_DRAFT_KEY);
  }

  clearRoadmapDraft(): void {
    this.clearDraft(this.ROADMAP_DRAFT_KEY);
  }

  // Кэширование документов базы знаний
  private readonly KNOWLEDGE_CACHE_KEY = 'dissai_knowledge_items_cache';
  private readonly CACHE_EXPIRY_KEY = 'dissai_knowledge_cache_expiry';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 минут

  saveKnowledgeCache(items: any[]): void {
    try {
      // Создаем облегченную версию документов (только ID, name, fileType, itemType)
      const lightweightItems = this.createLightweightItems(items);
      
      const cacheData = {
        items: lightweightItems,
        timestamp: Date.now()
      };
      const jsonData = JSON.stringify(cacheData);
      
      try {
        localStorage.setItem(this.KNOWLEDGE_CACHE_KEY, jsonData);
        localStorage.setItem(this.CACHE_EXPIRY_KEY, (Date.now() + this.CACHE_DURATION).toString());
      } catch (quotaError) {
        // Если localStorage переполнен, используем sessionStorage как запасной вариант
        console.warn('[LocalStorage] Quota exceeded, falling back to sessionStorage');
        sessionStorage.setItem(this.KNOWLEDGE_CACHE_KEY, jsonData);
        sessionStorage.setItem(this.CACHE_EXPIRY_KEY, (Date.now() + this.CACHE_DURATION).toString());
      }
      
      console.log('[LocalStorage] Knowledge cache saved:', items.length, 'items (lightweight version)');
    } catch (error) {
      console.error('[LocalStorage] Error saving knowledge cache:', error);
      // Если все еще не помещается, очищаем старые данные
      this.clearKnowledgeCache();
    }
  }

  private createLightweightItems(items: any[]): any[] {
    return items.map(item => {
      const lightItem: any = {
        id: item.id,
        name: item.name,
        itemType: item.itemType,
        fileType: item.fileType,
        parentId: item.parentId
      };
      
      // Рекурсивно обрабатываем children, если они есть
      if (item.children && item.children.length > 0) {
        lightItem.children = this.createLightweightItems(item.children);
      }
      
      return lightItem;
    });
  }

  getKnowledgeCache(): any[] | null {
    try {
      // Проверяем оба хранилища, приоритет у localStorage
      const expiryTime = localStorage.getItem(this.CACHE_EXPIRY_KEY) || sessionStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (!expiryTime || Date.now() > parseInt(expiryTime)) {
        console.log('[LocalStorage] Knowledge cache expired or not found');
        this.clearKnowledgeCache();
        return null;
      }

      const cacheData = localStorage.getItem(this.KNOWLEDGE_CACHE_KEY) || sessionStorage.getItem(this.KNOWLEDGE_CACHE_KEY);
      if (!cacheData) {
        console.log('[LocalStorage] No knowledge cache data found');
        return null;
      }

      const parsed = JSON.parse(cacheData);
      console.log('[LocalStorage] Knowledge cache loaded:', parsed.items?.length || 0, 'items');
      return parsed.items || null;
    } catch (error) {
      console.error('[LocalStorage] Error reading knowledge cache:', error);
      this.clearKnowledgeCache();
      return null;
    }
  }

  clearKnowledgeCache(): void {
    localStorage.removeItem(this.KNOWLEDGE_CACHE_KEY);
    localStorage.removeItem(this.CACHE_EXPIRY_KEY);
    sessionStorage.removeItem(this.KNOWLEDGE_CACHE_KEY);
    sessionStorage.removeItem(this.CACHE_EXPIRY_KEY);
    console.log('[LocalStorage] Knowledge cache cleared');
  }

  // Дополнительный метод для очистки всех старых данных
  clearAllCaches(): void {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dissai_')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      if (key !== this.USER_SESSION_KEY) { // Не удаляем сессию пользователя
        localStorage.removeItem(key);
      }
    });
    
    console.log('[LocalStorage] Cleared all caches except user session:', keysToRemove.length, 'items');
  }
}

// Экспорт экземпляра сервиса
export const localStorageService = new LocalStorageService(); 